import { useCallback, useEffect, useRef } from 'react';

/**
 * `useListRefreshWhenClean(refresh, paused)` → `requestRefresh()`.
 *
 * THE "the expanded panel just changed something the collapsed ROW shows —
 * refresh the list" call (owner, 2026-09-29, "fix all"): an attachment's
 * paperclip (`file_count`) and a Status Override's `can_action` are computed
 * ONLY by the list endpoint, so a detail-cache write cannot reach them and the
 * row waited for the 30 s poll.
 *
 * 🔑 `paused` is the page's OWN poll pause (`hooks/usePollingRefresh.js` rule
 * 1 — a dirty inline edit, Approvals' open reject form). A list refetch then
 * could re-bucket or drop the row under the unsaved work, which is exactly why
 * the poll skips. So while paused the request is REMEMBERED, never dropped and
 * never queued twice, and runs once when `paused` goes false. A Save already
 * refetches the list, so the deferred run after a save is one extra silent GET,
 * not a missing one.
 *
 * `paused` is read from a ref at CALL time: the callers invoke this after an
 * `await` (an upload, a PATCH), when the render that built the closure may be
 * stale. `requestRefresh` has a stable identity.
 *
 * @param {() => any} refresh  the page's SILENT list refetch
 * @param {boolean} paused     true while the page's poll is paused
 */
export default function useListRefreshWhenClean(refresh, paused) {
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;
  const pausedRef = useRef(paused);
  pausedRef.current = paused;
  const pendingRef = useRef(false);

  useEffect(() => {
    if (!paused && pendingRef.current) {
      pendingRef.current = false;
      refreshRef.current();
    }
  }, [paused]);

  return useCallback(() => {
    if (pausedRef.current) {
      pendingRef.current = true;
      return;
    }
    refreshRef.current();
  }, []);
}
